'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { constructQuery } from '@repo/shared/helpers';
import TasksTabById from 'src/parts/tabs/TasksTabById';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { getGroupTasks } from 'src/api/personal/groupDetail';

export const Route = createFileRoute('/personal_/groups/$groupId/tasks/')({
	validateSearch: (search: Record<string, unknown>) => ({
		page: (search.page as number) || undefined,
		sort: (search.sort as string) || undefined,
		filter: (search.filter as string) || undefined,
	}),
	loaderDeps: ({ search }) => search,
	loader: async ({ params, deps }) => {
		const query = constructQuery({ page: deps.page, sort: deps.sort, all: deps.filter === 'all' ? 'yes' : '' });
		const tasks = await getGroupTasks(params.groupId, query);
		return { tasks };
	},
	component: TasksPage,
});

function TasksPage() {
	const { groupId } = Route.useParams();
	const searchParams = Route.useSearch();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { tasks: initialTasks } = Route.useLoaderData();
	const { user: authenticatedUser } = useAuthenticatedUser();

	const query = constructQuery({
		page: searchParams.page,
		sort: searchParams.sort,
		all: searchParams.filter === 'all' ? 'yes' : '',
	});
	const { data: tasks, refetch: refetchTasks } = useQuery({
		queryKey: ['personal-group-tasks', groupId, searchParams.page, searchParams.sort, searchParams.filter],
		queryFn: () => getGroupTasks(groupId, query),
		initialData: initialTasks,
	});

	const handleUpdate = () => {
		refetchTasks();
		queryClient.invalidateQueries({ queryKey: ['personal-group', groupId] });
	};

	const handleReload = () => {
		refetchTasks();
	};

	return (
		<TasksTabById
			tasks={tasks}
			authenticatedUser={authenticatedUser!}
			onUpdate={handleUpdate}
			onReload={handleReload}
			onTaskSelected={(id) =>
				navigate({
					to: '/personal/groups/$groupId/tasks/$taskId',
					params: { groupId, taskId: id },
				})
			}
		/>
	);
}
